import { DomainRuleError } from "./core";

export const clinicalFactKeys = [
  "chief_complaint",
  "onset",
  "laterality",
  "anatomical_site",
  "trauma",
  "aggravating_factor",
  "associated_symptom",
  "vector_history",
  "history",
  "physical_examination",
  "order",
  "procedure",
] as const;
export type ClinicalFactKey = (typeof clinicalFactKeys)[number];

export const clinicalFactLabels: Readonly<Record<ClinicalFactKey, string>> = {
  chief_complaint: "주호소",
  onset: "발병시점",
  laterality: "좌우",
  anatomical_site: "부위",
  trauma: "외상 여부",
  aggravating_factor: "악화 동작",
  associated_symptom: "동반 증상",
  vector_history: "외상·손상기전",
  history: "현병력·과거력",
  physical_examination: "진찰 소견",
  order: "검사·치료 오더",
  procedure: "처치·시술",
};

export interface TranscriptEvidence {
  segmentId: string;
  quote: string;
}

export interface ClinicalFactSuggestion {
  id: string;
  key: ClinicalFactKey;
  label: string;
  value: string;
  confidence: number;
  source: "synthetic_rule" | "openai_realtime" | "openai_responses";
  evidence: TranscriptEvidence;
}

export interface VoiceTranscriptSegment {
  id: string;
  sessionId: string;
  encounterId: string;
  sequence: number;
  speakerRole: "conversation" | "clinician" | "patient" | "unknown";
  rawText: string;
  source: "synthetic_text" | "realtime_audio";
  capturedAt: string;
}

export interface VoiceChartingResult {
  sessionId: string;
  segment: VoiceTranscriptSegment;
  facts: ClinicalFactSuggestion[];
  mode: "synthetic_preview" | "realtime";
}

export function normalizeTranscript(value: string): string {
  const normalized = value.replace(/\r\n?/g, "\n").replace(/\s+/g, " ").trim();
  if (!normalized || normalized.length > 8_000 || normalized.includes("\u0000")) {
    throw new DomainRuleError(
      "INVALID_TRANSCRIPT",
      "문답 원문은 1~8,000자로 입력하십시오.",
    );
  }
  return normalized;
}

function evidenceQuote(text: string, pattern: RegExp): string {
  const match = pattern.exec(text);
  return match?.[0]?.trim() || text.slice(0, 120);
}

export function extractSyntheticClinicalFacts(
  rawText: string,
  segmentId: string,
): ClinicalFactSuggestion[] {
  const text = normalizeTranscript(rawText);
  const facts: ClinicalFactSuggestion[] = [];
  const seen = new Set<string>();

  const add = (
    key: ClinicalFactKey,
    value: string,
    confidence: number,
    pattern: RegExp,
  ): void => {
    const identity = `${key}\u0000${value}`;
    if (seen.has(identity)) return;
    seen.add(identity);
    facts.push({
      id: crypto.randomUUID(),
      key,
      label: clinicalFactLabels[key],
      value,
      confidence,
      source: "synthetic_rule",
      evidence: { segmentId, quote: evidenceQuote(text, pattern) },
    });
  };

  const onsetPattern = /(\d{1,3})\s*(년|개월|주|일)\s*(전부터|전|동안)/;
  const onset = onsetPattern.exec(text);
  if (onset?.[1] && onset[2]) {
    add("onset", `${onset[1]}${onset[2]} 전부터`, 0.99, onsetPattern);
  }

  const sitePattern = /(우측|오른쪽|좌측|왼쪽|양측)?\s*(어깨|무릎|고관절|팔꿈치|손목|발목|허리|목)\s*(?:가|이)?\s*(?:아프|아파|아픈|통증)/;
  const site = sitePattern.exec(text);
  if (site?.[2]) {
    const side = site[1];
    const laterality =
      side === "우측" || side === "오른쪽"
        ? "우측"
        : side === "좌측" || side === "왼쪽"
          ? "좌측"
          : side === "양측"
            ? "양측"
            : "미상";
    if (laterality !== "미상") add("laterality", laterality, 0.99, sitePattern);
    add("anatomical_site", site[2], 0.99, sitePattern);
    add(
      "chief_complaint",
      `${laterality === "미상" ? "" : `${laterality} `}${site[2]} 통증`,
      0.98,
      sitePattern,
    );
  }

  const noTraumaPattern = /(다치(?:지|지는)\s*않았|외상(?:력|은|이)?\s*(?:없|부인))/;
  if (noTraumaPattern.test(text)) {
    add("trauma", "외상 없음", 0.99, noTraumaPattern);
  }

  const aggravatingPattern = /(?:우측|오른쪽|좌측|왼쪽)?\s*팔(?:을)?\s*(?:들(?:어|고)?|올릴)\s*(?:때|면)?[^.?!]*(?:아프|아파|아픈|통증)/;
  if (aggravatingPattern.test(text)) {
    const laterality = facts.find((fact) => fact.key === "laterality")?.value;
    add(
      "aggravating_factor",
      `${laterality ? `${laterality} ` : ""}팔을 들 때 통증`,
      0.97,
      aggravatingPattern,
    );
  }

  const nightPainPattern = /야간통|밤(?:에|이면)[^.?!]*(?:아프|통증)/;
  if (nightPainPattern.test(text)) {
    add("associated_symptom", "야간통", 0.96, nightPainPattern);
  }

  return facts;
}

export function syntheticShoulderDialogue(): string {
  return [
    "어디가 아파요?",
    "3개월 전부터 아픕니다.",
    "어디가 아파요?",
    "우측 어깨가 아파요.",
    "다쳤습니까?",
    "다치지는 않았어요.",
    "팔 들어보세요. 어떨 때 가장 아파요?",
    "우측 팔을 들 때가 가장 아파요.",
  ].join(" ");
}
